import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store/store";
import libraryService from "@apis/libraryService";
import { CommonApiData } from "@type/common";
import { createAsyncThunkWithTraceID } from "@lib/redux";
import { Status } from "@models/common";
import {
  AspectRatio,
  VideoDuration,
  VideoModel,
  VideoQuantity,
  VideoResolution,
} from "@pages/videoCreating/videoCreatingSlice";
import report from "@report";
import { getUserAssets, fetchProductList as newFetchProductList } from "@apis/http/api";
import { UserAiJobItemResponseStatus } from "@apis/generated/openapi";

const VIEWED_CARDS_KEY = "viewed_video_generate_cards";

// 获取已查看的卡片 ID 列表
export const getViewedCards = (): string[] => {
  try {
    const stored = localStorage.getItem(VIEWED_CARDS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// 标记卡片为已查看
export const markCardAsViewed = (id: string | number) => {
  try {
    const viewed = getViewedCards();
    if (!viewed.includes(String(id))) {
      viewed.push(String(id));
      localStorage.setItem(VIEWED_CARDS_KEY, JSON.stringify(viewed));
    }
  } catch (error) {
    console.error("Failed to save viewed card:", error);
  }
};

// 检查卡片是否已查看
export const isCardViewed = (id: string | number): boolean => {
  return getViewedCards().includes(String(id));
};

export type VideoGenerateStatus = "processing" | "queued" | "completed" | "failed";

export interface VideoGenerateRecord {
  id: string;
  jobItem: {
    context: {
      coverImageUrl: string;
      videoUrl: string;
      productImageUrl: string;
      productTitle: string;
      aspectRatio: AspectRatio;
      duration: VideoDuration;
      enhanceVideoUrl: null;
      quantity: VideoQuantity;
      resolution: VideoResolution;
      productCoverImageUrl: string;
    };
    status: VideoGenerateStatus;
    /** 老数据可能为空 */
    internalModelId?: VideoModel;
    [key: string]: any;
  };
  videoId?: string;
  videoSource?: string;
  productImageUrl?: string;
  productName?: string;
  status: VideoGenerateStatus;
  progress?: number; // 0-100，处理中时显示进度
  description?: string;
  collectionId?: number;
  createdAt?: string; // ISO 时间字符串，用于计算时间差
  gmtCreated?: string;
  gmtEstimatedCompleted?: string;
}

export const statusConfig: Record<UserAiJobItemResponseStatus, { label: string; color: string }> = {
  processing: { label: "Processing", color: "processing" },
  queued: { label: "Queued", color: "default" },
  completed: { label: "Completed", color: "success" },
  failed: { label: "Failed", color: "error" },
};

export interface LibraryState {
  // 视频生成列表
  videoGeneratedList: CommonApiData;
  lastViewedTimestamp: number; // 用于触发 selector 重新计算

  // focusList 相关状态
  focusType: string;
  videoType: string;
  cardStatus: Status;
  cardItems: any[];
  cardPageSize: number;
  cardPageNo: number;

  // 回收站弹窗状态
  trashDisplay: boolean;
}

// 视频生成任务列表
export const fetchVideoGenerateList = createAsyncThunk("library/fetchVideoGenerateList", async (data: any) => {
  return await getUserAssets({ assetType: "VID", ...data });
});

// 商品列表
export const fetchProductList = createAsyncThunk("library/fetchProductList", async () => {
  return await newFetchProductList();
});

// 脚本、视频收藏列表
export const fetchCollectionList = createAsyncThunkWithTraceID<any[], { type: string | number; data?: unknown }>(
  "library/fetchCollectionList",
  async ({ type }) => {
    return await libraryService.fetchCollectionList({ type });
  },
);

const initialState: LibraryState = {
  // focusList 相关初始状态
  focusType: "video",
  videoType: "generated",

  // 视频生成相关数据
  videoGeneratedList: {
    status: "idle",
    data: [],
    pagination: {
      hasNext: false,
      pageNo: 1,
      pageSize: 10,
      start: 0,
      total: 0,
    },
  },
  lastViewedTimestamp: 0,

  // 其他收藏列表相关数据
  cardStatus: "idle",
  cardItems: [],
  cardPageSize: 0,
  cardPageNo: 1,

  // 回收站弹窗状态
  trashDisplay: false,
};

const librarySlice = createSlice({
  name: "library",
  initialState,
  reducers: {
    // 更新已查看时间戳，触发 selector 重新计算
    updateViewedTimestamp: (state) => {
      state.lastViewedTimestamp = Date.now();
    },
    // focusList reducers
    resetPageNo: (_state) => {
      // placeholder for reset logic
    },
    updateFocusType: (state, action: PayloadAction<string>) => {
      state.focusType = action.payload;
    },
    updateVideoType: (state, action: PayloadAction<string>) => {
      state.videoType = action.payload;
    },
    updateCardPageSize: (state, action: PayloadAction<number>) => {
      state.cardPageSize = action.payload;
    },
    updateCardPageNo: (state, action: PayloadAction<number>) => {
      state.cardPageNo = action.payload;
    },
    showTrashDisplay: (state) => {
      state.trashDisplay = true;
    },
    hideTrashDisplay: (state) => {
      state.trashDisplay = false;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchVideoGenerateList.pending, (state) => {
        // 首次加载时设为 loading，轮询时保持 succeeded 避免闪烁
        if (state.videoGeneratedList.status === "idle") {
          state.cardStatus = "loading";
          state.videoGeneratedList.status = "loading";
        }
      })
      .addCase(fetchVideoGenerateList.fulfilled, (state, action: any) => {
        const newData = action.payload?.data ?? [];
        const oldData = state.videoGeneratedList.data ?? [];

        // 比较数据是否有变化（通过 id + status 快照），无变化则跳过更新以避免无意义的重渲染
        const toSnapshot = (list: any[]) =>
          list.map((item: any) => `${item.id}:${item.jobItem?.status}:${item.progress}`).join("|");
        const changed = toSnapshot(newData) !== toSnapshot(oldData);

        if (changed) {
          state.videoGeneratedList = { status: "succeeded", ...action.payload };
        } else {
          state.videoGeneratedList.status = "succeeded";
        }

        if (newData.length === 0) {
          state.cardStatus = "blank";
        } else {
          state.cardStatus = "succeeded";
        }
      })
      .addCase(fetchVideoGenerateList.rejected, (state, action) => {
        const isPolling = state.videoGeneratedList.status === "succeeded";
        report.event("Favorites_load", {
          kalo_type: isPolling ? "polling_failed" : "initial_failed",
          kalo_value: action.error?.message || action.error,
        });
        if (!isPolling) {
          state.videoGeneratedList.status = "failed";
          state.cardStatus = "failed";
        }
      })
      // 商品列表
      .addCase(fetchProductList.pending, (state) => {
        state.cardStatus = "loading";
      })
      .addCase(fetchProductList.fulfilled, (state, action: any) => {
        state.cardItems = action.payload;
        if (action.payload.length === 0) {
          state.cardStatus = "blank";
        } else {
          state.cardStatus = "succeeded";
        }
      })
      .addCase(fetchProductList.rejected, (state) => {
        state.cardStatus = "failed";
      })
      // 收藏列表
      .addCase(fetchCollectionList.pending, (state) => {
        state.cardStatus = "loading";
      })
      .addCase(fetchCollectionList.fulfilled, (state, action) => {
        state.cardItems = action.payload;
        if (action.payload.length === 0) {
          state.cardStatus = "blank";
        } else {
          state.cardStatus = "succeeded";
        }
      })
      .addCase(fetchCollectionList.rejected, (state) => {
        state.cardStatus = "failed";
      });
  },
});

export const {
  updateViewedTimestamp,
  resetPageNo,
  updateFocusType,
  updateVideoType,
  updateCardPageSize,
  updateCardPageNo,
  showTrashDisplay,
  hideTrashDisplay,
} = librarySlice.actions;

export const selectvideoGeneratedList = (state: RootState) => state.libraryReducer.videoGeneratedList;
export const selectGeneratedStatus = (state: RootState) => state.libraryReducer.videoGeneratedList.status;
export const selectProcessingItems = (state: RootState) =>
  state.libraryReducer.videoGeneratedList.data.filter((item) => {
    return ["processing", "queued"].includes(item.jobItem?.status);
  });

// 计算未查看（显示 New）的卡片数量
export const selectNewCardsCount = (state: RootState) => {
  // 依赖 lastViewedTimestamp 确保 localStorage 变化时重新计算
  state.libraryReducer.lastViewedTimestamp; // 触发依赖
  const viewedIds = getViewedCards();
  return state.libraryReducer.videoGeneratedList.data.filter((item) => !viewedIds.includes(item.id)).length;
};

export default librarySlice.reducer;
